/**
 * GRiPS Widget (GRAutomates Intelligent Personal Support)
 *
 * Self-contained floating chat widget that can be dropped into any authenticated view.
 * Appears in the bottom-right corner like enterprise chat solutions (Intercom, Zendesk).
 * Users can interact with both the chat and the page simultaneously.
 *
 * GRiPS helps employees get a grip on things — answering questions about
 * how to use GRAutomates features and navigate the system.
 *
 * Features:
 * - Floating bottom-right widget (no modal/backdrop blocking page interaction)
 * - FAB reappears when navigating to new pages
 * - Escape key and X button both minimize chat (keeps conversation)
 * - Character counter with limit warning
 * - Retry failed messages
 * - Request cancellation on close
 * - Accessible (ARIA live regions, keyboard navigation)
 * - Mobile optimized (FAB hides on scroll, responsive sizing)
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  getChatbotStatus,
  sendMessageStream,
  generateMessageId,
  cancelPendingRequest,
  CHATBOT_CONFIG,
  type ChatMessage,
} from '../../services/chatbotService';
import {
  PaperAirplaneIcon,
  TrashIcon,
  SparklesIcon,
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
  MinusIcon,
} from '@heroicons/react/24/outline';
import { useModalContext } from '../../contexts/ModalContext';
import CloseButton from '../shared/CloseButton';
import Button from '../shared/Button';
import { TEXT } from '../shared/Typography';

// ============================================================================
// Conversation Cache (localStorage with 30-minute TTL)
// ============================================================================

const CACHE_KEY = 'grips_conversation';
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

interface CachedConversation {
  messages: Array<{
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: string; // ISO string (Date doesn't survive JSON serialization)
    isError?: boolean;
    originalMessage?: string;
  }>;
  savedAt: number; // Date.now() timestamp
}

function saveConversation(messages: ChatMessage[]): void {
  try {
    // Only cache non-error user/assistant messages with content
    const toCache = messages.filter(m => m.content && !m.isError);
    if (toCache.length === 0) {
      localStorage.removeItem(CACHE_KEY);
      return;
    }
    const data: CachedConversation = {
      messages: toCache.map(m => ({
        id: m.id,
        role: m.role,
        content: m.content,
        timestamp: m.timestamp instanceof Date ? m.timestamp.toISOString() : String(m.timestamp),
        isError: m.isError,
        originalMessage: m.originalMessage,
      })),
      savedAt: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch (_err) {
    // localStorage full or unavailable — silently ignore
  }
}

function loadConversation(): ChatMessage[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const data: CachedConversation = JSON.parse(raw);
    // Check TTL
    if (Date.now() - data.savedAt > CACHE_TTL_MS) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    // Rehydrate Date objects
    return data.messages.map(m => ({
      ...m,
      timestamp: new Date(m.timestamp),
    }));
  } catch (_err) {
    localStorage.removeItem(CACHE_KEY);
    return null;
  }
}

function clearConversationCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (_err) {
    // Ignore
  }
}

export default function ChatbotWidget() {
  // Hide chatbot FAB and panel when any modal is open
  const { isAnyModalOpen } = useModalContext();
  // Track current page URL for page-specific dismissal
  // Note: App uses custom query-param routing, not React Router
  const [currentUrl, setCurrentUrl] = useState(() => window.location.href);

  // State
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, _setIsMinimized] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadConversation() || []);
  const [inputValue, setInputValue] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState<string | undefined>();
  const [hasCheckedStatus, setHasCheckedStatus] = useState(false);
  const [isFabVisible, setIsFabVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isDismissedForSession, setIsDismissedForSession] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Reset dismissal state when user navigates to a new page
  // This allows the FAB to reappear on each new page visit
  // Monitor URL changes using popstate + polling (for query param changes)
  useEffect(() => {
    const checkUrlChange = () => {
      const newUrl = window.location.href;
      if (newUrl !== currentUrl) {
        setCurrentUrl(newUrl);
        setIsDismissedForSession(false);
      }
    };

    // Handle browser back/forward
    window.addEventListener('popstate', checkUrlChange);

    // Poll for query param changes (since app uses custom routing)
    const interval = setInterval(checkUrlChange, 500);

    return () => {
      window.removeEventListener('popstate', checkUrlChange);
      clearInterval(interval);
    };
  }, [currentUrl]);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const liveRegionRef = useRef<HTMLDivElement>(null);

  // Computed values
  const charCount = inputValue.length;
  const isNearLimit = charCount >= CHATBOT_CONFIG.WARNING_THRESHOLD;
  const isOverLimit = charCount > CHATBOT_CONFIG.MAX_MESSAGE_LENGTH;

  // Check chatbot availability on mount
  useEffect(() => {
    let mounted = true;

    const checkStatus = async () => {
      try {
        const status = await getChatbotStatus();
        if (mounted) {
          setIsAvailable(status.available);
          setWelcomeMessage(status.welcomeMessage);
          setHasCheckedStatus(true);
        }
      } catch {
        if (mounted) {
          setIsAvailable(false);
          setHasCheckedStatus(true);
        }
      }
    };

    checkStatus();

    return () => {
      mounted = false;
    };
  }, []);

  // Add welcome message when chat opens for first time with no messages
  useEffect(() => {
    if (isOpen && messages.length === 0 && welcomeMessage) {
      setMessages([
        {
          id: generateMessageId(),
          role: 'assistant',
          content: welcomeMessage,
          timestamp: new Date(),
        },
      ]);
    }
  }, [isOpen, messages.length, welcomeMessage]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Persist conversation to localStorage on every message change
  useEffect(() => {
    saveConversation(messages);
  }, [messages]);

  // Focus input when chat opens (and not minimized)
  // On mobile, don't auto-focus to prevent keyboard from opening immediately
  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current && !isMobile) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, isMinimized, isMobile]);

  // Note: We no longer prevent body scroll - the floating widget allows
  // users to interact with both the chat and the page simultaneously

  // Handle escape key - just close panel but keep FAB visible (preserve conversation)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        // Just close the panel, don't dismiss the FAB
        setIsOpen(false);
        // Do NOT set isDismissedForSession - ESC should not hide the icon
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Hide FAB when scrolling down on mobile
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const isMobile = window.innerWidth < 640;

      if (isMobile) {
        if (currentScrollY > lastScrollY && currentScrollY > 100) {
          setIsFabVisible(false);
        } else {
          setIsFabVisible(true);
        }
      } else {
        setIsFabVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Announce new messages to screen readers
  const announceToScreenReader = useCallback((message: string) => {
    if (liveRegionRef.current) {
      liveRegionRef.current.textContent = message;
    }
  }, []);

  // Handle minimize - just hide chat panel, keep FAB visible
  const handleMinimize = useCallback(() => {
    setIsOpen(false);
    // Don't dismiss FAB - user can reopen the chat
  }, []);

  // Handle close - cancel any pending request, clear conversation, and dismiss FAB for session
  const handleClose = useCallback(() => {
    if (isLoading || isStreaming) {
      cancelPendingRequest();
      setIsLoading(false);
      setIsStreaming(false);
      setStreamingMessageId(null);
    }
    setIsOpen(false);
    setMessages([]);
    clearConversationCache();
    setIsDismissedForSession(true);
  }, [isLoading, isStreaming]);

  // Handle message submission with streaming
  const handleSubmit = useCallback(
    async (e?: React.FormEvent, retryMessage?: string) => {
      e?.preventDefault();

      const messageToSend = retryMessage || inputValue.trim();
      if (!messageToSend || isLoading || isStreaming || isOverLimit) return;

      // Clear input if not a retry
      if (!retryMessage) {
        setInputValue('');
        if (inputRef.current) {
          inputRef.current.style.height = 'auto';
        }
      }

      // Add user message immediately
      const userMessage: ChatMessage = {
        id: generateMessageId(),
        role: 'user',
        content: messageToSend,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, userMessage]);
      setIsLoading(true);
      announceToScreenReader('Sending message...');

      // Get history (excluding the message we just added)
      const history = messages.filter(m => !m.isError);

      // Create placeholder for assistant message that will be streamed
      const assistantMessageId = generateMessageId();
      let streamedContent = '';

      const response = await sendMessageStream(messageToSend, history, {
        onStart: () => {
          setIsLoading(false);
          setIsStreaming(true);
          setStreamingMessageId(assistantMessageId);

          // Add empty assistant message that will be updated
          const assistantMessage: ChatMessage = {
            id: assistantMessageId,
            role: 'assistant',
            content: '',
            timestamp: new Date(),
          };
          setMessages(prev => [...prev, assistantMessage]);
          announceToScreenReader('GRiPS is responding...');
        },

        onText: (text: string) => {
          streamedContent += text;
          // Update the assistant message content
          setMessages(prev =>
            prev.map(msg =>
              msg.id === assistantMessageId
                ? { ...msg, content: streamedContent }
                : msg
            )
          );
        },

        onDone: () => {
          setIsStreaming(false);
          setStreamingMessageId(null);
          announceToScreenReader('Response complete');
        },

        onError: (error: string) => {
          setIsLoading(false);
          setIsStreaming(false);
          setStreamingMessageId(null);

          // If we already started streaming, update the message to show error
          if (streamedContent) {
            setMessages(prev =>
              prev.map(msg =>
                msg.id === assistantMessageId
                  ? { ...msg, content: streamedContent || error, isError: !streamedContent, originalMessage: !streamedContent ? messageToSend : undefined }
                  : msg
              )
            );
          } else {
            // No content yet, add error message
            const errorMessage: ChatMessage = {
              id: assistantMessageId,
              role: 'assistant',
              content: error,
              timestamp: new Date(),
              isError: true,
              originalMessage: messageToSend,
            };
            setMessages(prev => {
              // Remove empty placeholder if exists
              const filtered = prev.filter(m => m.id !== assistantMessageId);
              return [...filtered, errorMessage];
            });
          }
          announceToScreenReader('Error: ' + error);
        },
      });

      // Handle cancelled request
      if (response.cancelled) {
        setIsLoading(false);
        setIsStreaming(false);
        setStreamingMessageId(null);
        // Remove the empty assistant message if cancelled before any content
        if (!streamedContent) {
          setMessages(prev => prev.filter(m => m.id !== assistantMessageId));
        }
        return;
      }

      // Final cleanup if somehow we're still loading
      setIsLoading(false);
    },
    [inputValue, isLoading, isStreaming, isOverLimit, messages, announceToScreenReader]
  );

  // Handle retry
  const handleRetry = useCallback(
    (originalMessage: string) => {
      // Remove the last error message
      setMessages(prev => prev.slice(0, -1));
      // Retry the original message
      handleSubmit(undefined, originalMessage);
    },
    [handleSubmit]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;
    setInputValue(textarea.value);
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    clearConversationCache();
    announceToScreenReader('Conversation cleared');
  }, [announceToScreenReader]);

  // Memoize character counter class
  const charCounterClass = useMemo(() => {
    if (isOverLimit) return 'text-red-600 font-medium';
    if (isNearLimit) return 'text-amber-600';
    return 'text-gray-500';
  }, [isOverLimit, isNearLimit]);

  // Don't render anything if chatbot is not available
  if (!hasCheckedStatus || !isAvailable) {
    return null;
  }

  return (
    <>
      {/* Screen reader live region */}
      <div
        ref={liveRegionRef}
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      />

      {/* Floating Action Button - hidden when dismissed for session or when any modal is open */}
      {!isOpen && !isDismissedForSession && !isAnyModalOpen && (
        <div
          className={`
            fixed z-[60]
            right-4 sm:right-6
            transition-all duration-300 ease-in-out
            ${isFabVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0 pointer-events-none'}
          `}
          style={{
            bottom: 'max(1rem, calc(env(safe-area-inset-bottom) + 1rem))',
          }}
        >
          {/* Dismiss button — hides FAB for the current page */}
          <CloseButton onClick={(e) => {
              e.stopPropagation();
              setIsDismissedForSession(true);
            }} size="xs" label="Hide chat button" title="Hide for this page" className="absolute -top-1.5 -right-1.5 sm:-top-1 sm:-right-1 z-10" />

          {/* Open chat button */}
          <button
            onClick={() => setIsOpen(true)}
            aria-label="Open chat assistant"
            title="Chat with GRiPS"
          >
            <div
              className="
                relative flex items-center justify-center
                w-10 h-10 sm:w-14 sm:h-14
                bg-blue-600 hover:bg-blue-700
                text-white rounded-full
                shadow-md hover:shadow-lg
                transform hover:scale-105 active:scale-95
                transition-all duration-200
                group
              "
            >
              <ChatBubbleLeftRightIcon className="w-5 h-5 sm:w-7 sm:h-7" />

              {/* Tooltip on desktop */}
              <span
                className="
                  absolute right-full mr-3
                  px-3 py-1.5
                  text-sm font-medium text-white
                  bg-gray-900 rounded-lg
                  whitespace-nowrap
                  opacity-0 group-hover:opacity-100
                  translate-x-2 group-hover:translate-x-0
                  transition-all duration-200
                  pointer-events-none
                  hidden sm:block
                "
              >
                Get a GRiPS!
                <span
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"
                  aria-hidden="true"
                />
              </span>
            </div>
          </button>
        </div>
      )}

      {/* Floating Chat Widget - Bottom right corner on all screen sizes, hidden when modal is open */}
      {isOpen && !isAnyModalOpen && (
        <div
          className="fixed z-[110] sm:bottom-6 sm:right-6"
          role="dialog"
          aria-modal="false"
          aria-labelledby="chatbot-title"
          style={{
            bottom: 'max(1rem, calc(env(safe-area-inset-bottom) + 1rem))',
            right: 'max(1rem, env(safe-area-inset-right))',
          }}
        >
          {/*
            Floating widget container:
            - Consistent floating panel in bottom-right corner on all screen sizes
            - No backdrop - allows page interaction while chat is open
            - Slightly larger on desktop for better readability
            - Mobile: constrained width to prevent overflow
          */}
          <div
            className="
              w-[min(calc(100vw-2rem),320px)] sm:w-[400px] sm:max-w-[400px]
              h-[60vh] max-h-[450px] sm:h-[520px] sm:max-h-[70vh]
              bg-white rounded-lg shadow-xl
              flex flex-col overflow-hidden
              animate-in slide-in-from-bottom-4 duration-200
            "
            style={{
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200 bg-blue-600">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="flex items-center justify-center w-7 h-7 sm:w-9 sm:h-9 bg-white/20 rounded-lg">
                  <SparklesIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div>
                  <h2 id="chatbot-title" className="text-sm sm:text-base font-semibold text-white">
                    GRiPS
                  </h2>
                  <p className="text-2xs sm:text-xs text-blue-100 hidden sm:block">GR Intelligent Personal Support</p>
                </div>
              </div>
              <div className="flex items-center gap-0.5 sm:gap-1">
                {messages.length > 1 && (
                  <Button variant="onDark" onClick={clearMessages} title="Clear conversation" aria-label="Clear conversation">
                    <TrashIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                  </Button>
                )}
                <Button variant="onDark" onClick={handleMinimize} title="Minimize (keep conversation)" aria-label="Minimize chat">
                  <MinusIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                </Button>
                <CloseButton onClick={handleClose} size="sm" label="Close chat" />
              </div>
            </div>

            {/* Messages area - compact padding on mobile */}
            <div
              className="flex-1 overflow-y-auto px-3 sm:px-4 py-3 sm:py-4 space-y-3 sm:space-y-4 bg-gray-50"
              role="log"
              aria-label="Chat messages"
            >
              {messages.map(message => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  isStreaming={message.id === streamingMessageId}
                  onRetry={message.isError && message.originalMessage ? () => handleRetry(message.originalMessage!) : undefined}
                />
              ))}

              {/* Loading indicator - shows before streaming starts */}
              {isLoading && !isStreaming && (
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <SparklesIcon className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="bg-white rounded-lg rounded-tl-sm px-4 py-3 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-2" aria-label="GRiPS is thinking">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                      <span className={`ml-1 ${TEXT.caption}`}>GRiPS is thinking...</span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input area - compact on mobile */}
            <div className="px-3 sm:px-4 py-2 sm:py-3 border-t border-gray-200 bg-white">
              <form onSubmit={handleSubmit} className="flex items-end gap-2">
                <div className="flex-1 relative">
                  <textarea
                    ref={inputRef}
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder={isStreaming ? "GRiPS is responding..." : "Type your question..."}
                    rows={1}
                    disabled={isLoading || isStreaming}
                    maxLength={CHATBOT_CONFIG.MAX_MESSAGE_LENGTH + 100} // Allow slight over for UX
                    className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 pr-10 sm:pr-12 text-sm border rounded-md resize-none focus:outline-none focus:ring-2 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50 ${
                      isOverLimit
                        ? 'border-red-300 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    style={{ minHeight: '40px', maxHeight: '100px' }}
                    aria-label="Message input"
                    aria-describedby="char-count"
                  />
                  <Button variant="primary" type="submit" disabled={!inputValue.trim() || isLoading || isStreaming || isOverLimit} aria-label="Send message" className="absolute right-1.5 bottom-1.5">
                    <PaperAirplaneIcon className="w-4 h-4" />
                  </Button>
                </div>
              </form>

              {/* Character counter - hidden on mobile for space, visible on desktop */}
              <div className="mt-1 sm:mt-2 flex items-center justify-end text-xs">
                <span id="char-count" className={charCounterClass}>
                  {charCount}/{CHATBOT_CONFIG.MAX_MESSAGE_LENGTH}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Individual message bubble component
 */
interface MessageBubbleProps {
  message: ChatMessage;
  isStreaming?: boolean;
  onRetry?: () => void;
}

function MessageBubble({ message, isStreaming = false, onRetry }: MessageBubbleProps) {
  const isUser = message.role === 'user';
  const isError = message.isError;

  return (
    <div className={`flex items-start gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      {!isUser && (
        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
          isError ? 'bg-red-100' : 'bg-blue-100'
        }`}>
          <SparklesIcon className={`w-4 h-4 ${isError ? 'text-red-600' : 'text-blue-600'}`} />
        </div>
      )}

      {/* Message bubble */}
      <div className="flex flex-col gap-1 max-w-[85%]">
        <div
          className={`rounded-lg px-4 py-2.5 ${
            isUser
              ? 'bg-blue-600 text-white rounded-tr-sm'
              : isError
              ? 'bg-red-50 text-red-800 rounded-tl-sm border border-red-200'
              : 'bg-white text-gray-800 rounded-tl-sm shadow-sm border border-gray-100'
          }`}
        >
          <div className="text-sm whitespace-pre-wrap break-words">
            <FormattedContent content={message.content} />
            {/* Streaming cursor indicator */}
            {isStreaming && (
              <span className="inline-block w-2 h-4 ml-0.5 bg-blue-500 animate-pulse rounded-sm" aria-hidden="true" />
            )}
          </div>
        </div>

        {/* Retry button for error messages */}
        {isError && onRetry && (
          <Button variant="linkDanger" size="xs" onClick={onRetry} aria-label="Retry sending message" className="self-start ml-1">
            <ArrowPathIcon className="w-3.5 h-3.5" />
            <span>Retry</span>
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Formatted content component - renders markdown-like formatting safely
 * Uses React elements directly instead of dangerouslySetInnerHTML to prevent XSS
 */
function FormattedContent({ content }: { content: string }) {
  const formattedContent = useMemo(() => {
    const lines = content.split('\n');

    return lines.map((line, lineIdx) => {
      // Check for numbered list item
      const numberedMatch = line.match(/^(\d+)\.\s+(.+)$/);
      if (numberedMatch) {
        return (
          <div key={`line-${lineIdx}`} className="flex gap-2">
            <span className="text-gray-500 shrink-0">{numberedMatch[1]}.</span>
            <span>{renderInlineFormatting(numberedMatch[2])}</span>
          </div>
        );
      }

      // Check for bullet list item (-, •, or *)
      const bulletMatch = line.match(/^[-•*]\s+(.+)$/);
      if (bulletMatch) {
        return (
          <div key={`line-${lineIdx}`} className="flex gap-2">
            <span className="text-gray-500 shrink-0">•</span>
            <span>{renderInlineFormatting(bulletMatch[1])}</span>
          </div>
        );
      }

      // Regular line with content
      if (line.trim()) {
        return <div key={`line-${lineIdx}`}>{renderInlineFormatting(line)}</div>;
      }

      // Empty line - add spacing
      return <div key={`line-${lineIdx}`} className="h-2" />;
    });
  }, [content]);

  return <>{formattedContent}</>;
}

/**
 * Safely render inline formatting (bold, code) without using dangerouslySetInnerHTML
 * Returns an array of React nodes
 */
function renderInlineFormatting(text: string): React.ReactNode[] {
  const result: React.ReactNode[] = [];
  let keyIdx = 0;

  // Pattern to match bold (**text**) or inline code (`text`)
  const inlinePattern = /(\*\*[^*]+\*\*|`[^`]+`)/g;

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = inlinePattern.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      result.push(<span key={`text-${keyIdx++}`}>{text.slice(lastIndex, match.index)}</span>);
    }

    const matched = match[0];

    // Bold text
    if (matched.startsWith('**') && matched.endsWith('**')) {
      const boldText = matched.slice(2, -2);
      result.push(
        <strong key={`bold-${keyIdx++}`} className="font-semibold">
          {boldText}
        </strong>
      );
    }
    // Inline code
    else if (matched.startsWith('`') && matched.endsWith('`')) {
      const codeText = matched.slice(1, -1);
      result.push(
        <code
          key={`code-${keyIdx++}`}
          className="px-1.5 py-0.5 bg-gray-100 text-gray-800 rounded text-xs font-mono"
        >
          {codeText}
        </code>
      );
    }

    lastIndex = match.index + matched.length;
  }

  // Add remaining text after last match
  if (lastIndex < text.length) {
    result.push(<span key={`text-${keyIdx++}`}>{text.slice(lastIndex)}</span>);
  }

  // If no matches found, return the original text
  if (result.length === 0) {
    return [<span key="text-0">{text}</span>];
  }

  return result;
}
